import { Component, Input, Output, EventEmitter, SimpleChanges } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { EventResponse, EventClassResponse } from '../../../../../../../../@shared/models/response.module';
import { EventsService } from '../../../../../../../../@shared/services/events-service';
import { AddClass } from '../../../manage-event/components/manage-event-classes/components/add-class/add-class';
import { EditClass } from '../../../manage-event/components/manage-event-classes/components/edit-class/edit-class';
import { CopyClassModal } from '../../../manage-event/components/manage-event-classes/components/copy-class-modal/copy-class-modal';
import { AQH_EVE_6 } from '../../../../../../../../@shared/models/input.module';
import { TranslateService } from '@ngx-translate/core';
import { DeleteModal } from '../../../../../../../../@shared/components/delete-modal/delete-modal';
import { ToastService } from '../../../../../../../../@core/services/toast.service';

interface EventDay {
  date: Date;
  label: string;
  shortLabel: string;
  isToday: boolean;
}

@Component({
  selector: 'app-wizard-step-classes',
  standalone: false,
  templateUrl: './wizard-step-classes.html',
  styleUrl: './wizard-step-classes.css'
})
export class WizardStepClasses {
  @Input() event: EventResponse | null = null;
  @Output() classesChanged = new EventEmitter<number>();

  classes: EventClassResponse[] = [];
  filteredClasses: EventClassResponse[] = [];
  private lastLoadedEventId: number | null = null;

  // Day tabs
  eventDays: EventDay[] = [];
  selectedDay: Date | null = null;

  constructor(
    private modalService: NgbModal,
    private eventService: EventsService,
    private translateService: TranslateService,
    private toastService: ToastService
  ) {}

  ngOnInit() {
    this.loadClasses();
  }

  ngOnChanges(changes: SimpleChanges) {
    // Only reload if the event_id actually changed
    if (changes['event'] && this.event?.event_id && this.event.event_id !== this.lastLoadedEventId) {
      this.generateEventDays();
      this.loadClasses();
    }
  }

  generateEventDays() {
    if (!this.event?.start_date || !this.event?.end_date) {
      return;
    }

    const startDate = new Date(this.event.start_date);
    const endDate = new Date(this.event.end_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    this.eventDays = [];
    const currentDate = new Date(startDate);
    currentDate.setHours(0, 0, 0, 0);

    while (currentDate <= endDate) {
      const dayDate = new Date(currentDate);
      const isToday = dayDate.getTime() === today.getTime();

      this.eventDays.push({
        date: dayDate,
        label: this.formatDayLabel(dayDate),
        shortLabel: this.formatShortDayLabel(dayDate),
        isToday: isToday
      });

      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Select first day by default
    if (this.eventDays.length > 0 && !this.selectedDay) {
      this.selectedDay = this.eventDays[0].date;
    }
  }

  formatDayLabel(date: Date): string {
    const weekday = date.toLocaleDateString('nb-NO', { weekday: 'long' });
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(-2);
    return `${weekday} - ${day}.${month}.${year}`;
  }

  formatShortDayLabel(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    return `${day}.${month}`;
  }

  selectDay(day: EventDay) {
    this.selectedDay = day.date;
    this.filterClassesByDay();
  }

  filterClassesByDay() {
    if (!this.classes || !this.selectedDay) {
      this.filteredClasses = this.classes || [];
      return;
    }

    this.filteredClasses = this.classes.filter(eventClass => {
      if (!eventClass.start_date_time) return false;
      const classDate = new Date(eventClass.start_date_time);
      return this.isSameDay(classDate, this.selectedDay!);
    });
  }

  isSameDay(date1: Date, date2: Date): boolean {
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate();
  }

  getClassCountForDay(day: EventDay): number {
    if (!this.classes) return 0;
    return this.classes.filter(eventClass => {
      if (!eventClass.start_date_time) return false;
      const classDate = new Date(eventClass.start_date_time);
      return this.isSameDay(classDate, day.date);
    }).length;
  }

  loadClasses() {
    if (!this.event?.event_id) return;

    this.lastLoadedEventId = this.event.event_id;
    this.generateEventDays();
    const params: AQH_EVE_6 = { event_id: this.event.event_id };
    this.eventService.getEventClasses(params).subscribe((classes) => {
      this.classes = classes || [];
      this.filterClassesByDay();
      this.classesChanged.emit(this.classes.length);
    });
  }

  openAddClassModal() {
    if (!this.event) return;

    const modalRef = this.modalService.open(AddClass, { size: 'lg', centered: true });
    modalRef.componentInstance.event = this.event;
    modalRef.componentInstance.selectedDate = this.selectedDay;

    modalRef.result.then((result) => {
      if (result) {
        this.loadClasses();
      }
    }).catch(() => {});
  }

  openCopyClassModal(eventClass: EventClassResponse) {
    if (!this.event) return;

    const modalRef = this.modalService.open(CopyClassModal, { size: 'md', centered: true });
    modalRef.componentInstance.eventClass = eventClass;
    modalRef.componentInstance.event = this.event;

    modalRef.result.then((result) => {
      if (result) {
        this.loadClasses();
      }
    }).catch(() => {});
  }

  openEditClassModal(eventClass: EventClassResponse) {
    if (!this.event) return;

    const modalRef = this.modalService.open(EditClass, { size: 'lg', centered: true });
    modalRef.componentInstance.event = this.event;
    modalRef.componentInstance.eventClass = eventClass;

    modalRef.result.then((result) => {
      if (result) {
        this.loadClasses();
      }
    }).catch(() => {});
  }

  deleteClass(eventClass: EventClassResponse) {
    if (!this.event) return;

    const modalRef = this.modalService.open(DeleteModal, { size: 'md', centered: true });
    modalRef.componentInstance.description = this.translateService.instant('eventWizard.classes.deleteConfirm', { name: eventClass.class_name });

    modalRef.result.then((result) => {
      if (result) {
        this.eventService.deleteEventClass({
          event_id: this.event!.event_id,
          event_class_id: eventClass.event_class_id
        }).subscribe({
          next: () => {
            this.toastService.success(this.translateService.instant('eventWizard.classes.deleteSuccess'));
            this.loadClasses();
          },
          error: () => {
            this.toastService.error(this.translateService.instant('eventWizard.classes.deleteError'));
          }
        });
      }
    }).catch(() => {});
  }

  formatDateTime(dateValue: string | Date): string {
    if (!dateValue) return '-';
    const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
    return date.toLocaleString('nb-NO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
}
